import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import questionsData from '../data/questions.json'
import { ANSWER_HINTS } from '../data/answerHints'
import { AREAS } from '../data/constants'

const normalizeAnswer = (answer = '') => answer.toString().trim().toLowerCase()

const flattenAnswers = (answers = []) =>
  answers.flatMap((answer) => (Array.isArray(answer) ? answer : [answer]))

const renderEnglishWithHighlights = (template = '') => {
  const parts = []
  let lastIndex = 0
  let answerIndex = 0

  const regex = /\(([^)]+)\)/g
  let match

  while ((match = regex.exec(template)) !== null) {
    if (match.index > lastIndex) {
      parts.push({
        type: 'text',
        content: template.slice(lastIndex, match.index),
      })
    }

    const answerText = match[1].trim()
    parts.push({
      type: 'answer',
      content: answerText,
      index: answerIndex++,
    })

    lastIndex = match.index + match[0].length
  }

  if (lastIndex < template.length) {
    parts.push({
      type: 'text',
      content: template.slice(lastIndex),
    })
  }

  return parts
}


const buildAnswerInsights = (normalizedAnswers = []) => {
  const seen = new Set()

  return normalizedAnswers.reduce((acc, answerKey) => {
    if (!answerKey || seen.has(answerKey)) return acc
    const hint = ANSWER_HINTS[answerKey]
    if (!hint) return acc

    seen.add(answerKey)
    acc.push({
      key: answerKey,
      description: hint.description,
    })
    return acc
  }, [])
}

const TOTAL_QUESTIONS = questionsData.length
const MIN_QUESTION = questionsData[0]?.id ?? 1
const MAX_QUESTION = questionsData[questionsData.length - 1]?.id ?? MIN_QUESTION
const DEFAULT_RANGE = AREAS[0]?.range ?? [MIN_QUESTION, Math.min(MIN_QUESTION + 19, MAX_QUESTION)]


export default function FlashcardScreen({ questionRange: initialRange, onExit }) {
  const [questionRange, setQuestionRange] = useState(initialRange || DEFAULT_RANGE)
  const [questions, setQuestions] = useState([])
  const [speakingId, setSpeakingId] = useState(null)
  const [speechSupported, setSpeechSupported] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [expandedId, setExpandedId] = useState(null)
  const [activeAnswerIndex, setActiveAnswerIndex] = useState(null)
  const [activeRangeTab, setActiveRangeTab] = useState(null) // 選択中の範囲タブ
  const listRef = useRef(null)

  // 範囲タブの定義（20問ずつ）
  const rangeTabs = useMemo(() => {
    const tabs = []
    const itemsPerTab = 20
    for (let i = MIN_QUESTION; i <= MAX_QUESTION; i += itemsPerTab) {
      const rangeStart = i
      const rangeEnd = Math.min(i + itemsPerTab - 1, MAX_QUESTION)
      tabs.push({
        start: rangeStart,
        end: rangeEnd,
        label: `${rangeStart}〜${rangeEnd}`,
      })
    }
    return tabs
  }, [])

  // 初期範囲タブを設定
  useEffect(() => {
    if (questionRange) {
      const [rangeStart, rangeEnd] = questionRange
      // 範囲が完全に含まれるタブを探す
      let matchingTab = rangeTabs.find(tab => 
        tab.start <= rangeStart && rangeEnd <= tab.end
      )
      // 見つからない場合は、範囲の開始位置を含む最初のタブを選択
      if (!matchingTab) {
        matchingTab = rangeTabs.find(tab => 
          tab.start <= rangeStart && rangeStart <= tab.end
        )
      }
      // それでも見つからない場合は、最初のタブを選択
      if (!matchingTab) {
        matchingTab = rangeTabs[0]
      }
      // 現在の範囲タブと異なる場合のみ更新
      if (!activeRangeTab || activeRangeTab.start !== matchingTab.start || activeRangeTab.end !== matchingTab.end) {
        setActiveRangeTab(matchingTab)
      }
    } else {
      // デフォルトで最初のタブを選択
      const firstTab = rangeTabs[0]
      if (!activeRangeTab || activeRangeTab.start !== firstTab.start || activeRangeTab.end !== firstTab.end) {
        setActiveRangeTab(firstTab)
      }
    }
  }, [rangeTabs, questionRange])

  // 範囲タブが変更されたときにquestionRangeを更新
  useEffect(() => {
    if (activeRangeTab) {
      setQuestionRange([activeRangeTab.start, activeRangeTab.end])
    }
  }, [activeRangeTab])

  // Filter questions by range
  useEffect(() => {
    let filteredQuestions = [...questionsData]
    
    if (questionRange) {
      const [rangeStart, rangeEnd] = questionRange
      filteredQuestions = questionsData.filter(q => q.id >= rangeStart && q.id <= rangeEnd)
    }
    
    setQuestions(filteredQuestions)
  }, [questionRange])

  // Check if speech synthesis is supported
  useEffect(() => {
    if (!('speechSynthesis' in window)) {
      setSpeechSupported(false)
    }
  }, [])

  // Filter by search query
  const filteredQuestions = questions.filter(q => {
    if (!searchQuery) return true
    const query = searchQuery.toLowerCase()
    return (
      q.japanese.toLowerCase().includes(query) ||
      q.full_answer.toLowerCase().includes(query) ||
      q.answers.some(a => 
        (Array.isArray(a) ? a.some(ans => ans.toLowerCase().includes(query)) : a.toLowerCase().includes(query))
      )
    )
  })

  const speakText = useCallback((text, id) => {
    if (!speechSupported || !text) return

    // Cancel any ongoing speech
    window.speechSynthesis.cancel()

    const utterance = new SpeechSynthesisUtterance(text)
    utterance.lang = 'en-US'
    utterance.rate = 0.85
    utterance.pitch = 1

    utterance.onstart = () => setSpeakingId(id)
    utterance.onend = () => setSpeakingId(null)
    utterance.onerror = () => setSpeakingId(null)

    window.speechSynthesis.speak(utterance)
  }, [speechSupported])

  const handleSpeak = (question) => {
    speakText(question.full_answer, question.id)
  }

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id)
  }


  return (
    <div className="w-full h-full bg-gradient-to-br from-pink-100 via-purple-100 to-blue-100 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 flex-shrink-0 border-b-2 border-purple-300 bg-white/60">
        <button
          onClick={() => {
            window.speechSynthesis.cancel()
            onExit()
          }}
          className="text-purple-600 hover:text-pink-500 transition-colors text-lg sm:text-xl font-bold"
        >
          ✕
        </button>
        <h1 className="font-display text-lg text-purple-600 font-black">
          📚 フレーズリスト
        </h1>
        <span className="text-purple-600 text-sm font-bold">
          {filteredQuestions.length}件
        </span>
      </div>

      {/* Range Tabs */}
      <div className="px-4 sm:px-6 py-3 border-b-2 border-purple-300 flex-shrink-0 bg-white/40">
        <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide pb-2">
          {rangeTabs.map((tab) => (
            <button
              key={`${tab.start}-${tab.end}`}
              onClick={() => setActiveRangeTab(tab)}
              className={`px-4 py-2 rounded-xl text-sm font-display transition-all whitespace-nowrap flex-shrink-0 font-bold ${
                activeRangeTab && activeRangeTab.start === tab.start && activeRangeTab.end === tab.end
                  ? 'bg-gradient-to-r from-pink-400 to-purple-400 text-white border-2 border-pink-500 shadow-lg'
                  : 'text-purple-600 hover:text-pink-600 hover:bg-white/60 border-2 border-transparent bg-white/40'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-auto">
        <>

          {/* Search Bar */}
          <div className="px-4 sm:px-6 py-3 flex-shrink-0">
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="🔍 日本語・英語で検索..."
                className="w-full bg-white/80 border-2 border-purple-300 rounded-xl px-4 py-3 text-purple-700 placeholder-purple-400 focus:outline-none focus:border-pink-400 focus:bg-white focus:ring-2 focus:ring-pink-300 transition-all font-semibold"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-purple-400 hover:text-pink-500 font-bold"
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          {/* Phrase List */}
          <div 
            ref={listRef}
            className="flex-1 overflow-y-auto px-4 sm:px-6 pb-6"
          >
            <div className="space-y-3">
              {filteredQuestions.map((question, index) => {
                const isExpanded = expandedId === question.id
                const isSpeaking = speakingId === question.id
                const normalizedAnswers = flattenAnswers(question.answers).map(normalizeAnswer)
                const englishParts = renderEnglishWithHighlights(question.english_template)
                const answerInsights = buildAnswerInsights(normalizedAnswers)

                return (
                  <motion.div
                    key={question.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.02, duration: 0.3 }}
                    className={`rounded-xl border-2 transition-all duration-300 ${
                      isExpanded 
                        ? 'bg-gradient-to-br from-pink-200 to-purple-200 border-pink-400 shadow-lg' 
                        : 'bg-white/80 border-purple-300 hover:border-pink-400 hover:shadow-md'
                    }`}
                  >
                    {/* Main Content - Always Visible */}
                    <div 
                      className="p-4 cursor-pointer"
                      onClick={() => toggleExpand(question.id)}
                    >
                      {/* Number Badge */}
                      <div className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-pink-400 to-purple-400 text-white text-xs font-display flex items-center justify-center font-black shadow-lg">
                          {question.id}
                        </span>
                        
                        <div
                          className="flex-1 min-w-0"
                          onMouseLeave={() => setActiveAnswerIndex(null)}
                          onTouchEnd={() => setActiveAnswerIndex(null)}
                        >
                          {/* Japanese */}
                          <p className="text-purple-700 text-base sm:text-lg mb-2 leading-relaxed font-bold">
                            {question.japanese}
                          </p>
                            
                            {/* English with highlighted answers */}
                            <p className="text-sm sm:text-base leading-relaxed">
                              {englishParts.map((part, i) =>
                                part.type === 'answer' ? (
                                  <span
                                    key={`en-${question.id}-${i}`}
                                    className={`inline-block px-1.5 py-0.5 rounded-xl mx-0.5 font-bold border-2 transition-all duration-200 ${
                                      activeAnswerIndex === part.index
                                        ? 'bg-gradient-to-r from-pink-400 to-purple-400 text-white border-pink-500 shadow-lg scale-110'
                                        : 'bg-gradient-to-r from-pink-200 to-purple-200 text-purple-700 border-pink-400'
                                    }`}
                                    title={ANSWER_HINTS[normalizeAnswer(part.content)]?.description}
                                    onMouseEnter={() => setActiveAnswerIndex(part.index)}
                                    onFocus={() => setActiveAnswerIndex(part.index)}
                                    onTouchStart={() => setActiveAnswerIndex(part.index)}
                                    onBlur={() => setActiveAnswerIndex(null)}
                                    tabIndex={0}
                                  >
                                    {part.content}
                                  </span>
                                ) : (
                                  <span key={`en-${question.id}-text-${i}`} className="text-purple-600">
                                    {part.content}
                                  </span>
                                )
                              )}
                            </p>
                          </div>
                          
                          {/* Speak Button */}
                          {speechSupported && (
                            <motion.button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleSpeak(question)
                              }}
                              className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                                isSpeaking
                                  ? 'bg-gradient-to-br from-pink-500 to-purple-500 text-white shadow-lg'
                                  : 'bg-gradient-to-br from-pink-300 to-purple-300 text-purple-700 hover:from-pink-400 hover:to-purple-400'
                              }`}
                              whileTap={{ scale: 0.9 }}
                            >
                              <motion.span
                                animate={isSpeaking ? { scale: [1, 1.2, 1] } : {}}
                                transition={{ duration: 0.5, repeat: isSpeaking ? Infinity : 0 }}
                              >
                                🔊
                              </motion.span>
                            </motion.button>
                          )}
                        </div>
                      </div>

                      {/* Expanded Content - Answer Details */}
                      <AnimatePresence>
                        {isExpanded && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="overflow-hidden"
                          >
                            <div className="px-4 pb-4 pt-2 border-t-2 border-purple-300">
                              {/* Full Answer */}
                              <div className="mb-4">
                                <div className="text-xs text-purple-600 mb-1 font-display font-bold">完全な文</div>
                                <p className="text-purple-700 text-lg font-bold">
                                  {question.full_answer}
                                </p>
                              </div>
                              
                              {/* Answer Words Breakdown */}
                              <div className="mb-4">
                                <div className="text-xs text-purple-600 mb-2 font-display font-bold">穴埋め部分</div>
                                <div className="flex flex-wrap gap-2">
                                  {question.answers.map((answer, i) => (
                                    <span
                                      key={i}
                                      className="inline-flex items-center gap-1 bg-gradient-to-r from-pink-200 to-purple-200 text-purple-700 px-3 py-1.5 rounded-xl text-sm font-bold border-2 border-pink-400"
                                    >
                                      <span className="text-pink-600 text-xs">{i + 1}.</span>
                                      {Array.isArray(answer) ? answer.join(' / ') : answer}
                                    </span>
                                  ))}
                                </div>
                              </div>

                              {/* Japanese Meaning Insights */}
                              {answerInsights.length > 0 && (
                                <div className="mb-4">
                                  <div className="text-xs text-purple-600 mb-2 font-display font-bold">日本語ヒント</div>
                                  <div className="grid gap-2 sm:grid-cols-2">
                                    {answerInsights.map((hint) => (
                                      <div
                                        key={`${question.id}-${hint.key}`}
                                        className="rounded-xl border-2 border-purple-300 bg-white/80 p-3"
                                      >
                                        <div className="text-[10px] uppercase tracking-[0.2em] text-purple-500 mb-1 font-bold">
                                          {hint.key}
                                        </div>
                                        <p className="text-xs sm:text-sm text-purple-700 leading-relaxed font-semibold">
                                          {hint.description}
                                        </p>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Slow Pronunciation Button */}
                              {speechSupported && (
                                <motion.button
                                  onClick={() => {
                                    window.speechSynthesis.cancel()
                                    const utterance = new SpeechSynthesisUtterance(question.full_answer)
                                    utterance.lang = 'en-US'
                                    utterance.rate = 0.6 // Slower rate
                                    utterance.pitch = 1
                                    utterance.onstart = () => setSpeakingId(question.id)
                                    utterance.onend = () => setSpeakingId(null)
                                    window.speechSynthesis.speak(utterance)
                                  }}
                                  className="w-full py-2.5 rounded-xl bg-gradient-to-r from-blue-300 to-purple-300 text-purple-700 hover:from-blue-400 hover:to-purple-400 transition-colors text-sm font-display font-bold flex items-center justify-center gap-2 border-2 border-purple-400"
                                  whileHover={{ scale: 1.05 }}
                                  whileTap={{ scale: 0.95 }}
                                >
                                  🐢 ゆっくり発音
                                </motion.button>
                              )}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  )
                })}
              </div>
              
              {filteredQuestions.length === 0 && (
                <div className="text-center text-purple-600 py-12">
                  <p className="text-4xl mb-4">🔍</p>
                  <p className="font-bold">該当するフレーズが見つかりません</p>
                </div>
              )}
            </div>
        </>
      </div>

      {/* Bottom Hint */}
      <div className="text-center text-purple-600 text-xs py-3 border-t-2 border-purple-300 flex-shrink-0 font-bold bg-white/60">
        タップで詳細を表示 / 🔊 で発音確認
      </div>
    </div>
  )
}
